import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import { humanizeField } from "../utils/stringUtils";

export interface TableColumn {
  header: string;
  accessor?: string;
  isAction?: boolean;
  actionButtons?: ActionButton[];
  render?: (row: any) => React.ReactNode;
}

export interface ActionButton {
  id: string;
  label: string;
  icon?: string;
  class?: string;
  permissions: string[];
  action: (row: any) => void;
}

interface TableProps {
  columns: TableColumn[];
  data: any[];
  loading?: boolean;
  pagination?: {
    page: number;
    last_page: number;
    page_size: number;
    total_items: number;
    is_first_page: boolean;
    is_last_page: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
  };
  searchFields?: string[];
  onSearch?: (field: string, query: string) => void;
}

const rowVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, x: -50, transition: { duration: 0.4 } },
};

const tableVariants = {
  hidden: { opacity: 1 },
  visible: { transition: { staggerChildren: 0.2 } },
};

const PaginatedTable: React.FC<TableProps> = ({
  columns,
  data,
  loading = false,
  pagination,
  searchFields,
  onSearch,
}) => {
  const { theme } = useTheme();
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const handleMenuToggle = (index: number) => {
    setOpenMenuIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  const [searchKey, setSearchKey] = useState(searchFields?.[0] || "");
  const [searchInput, setSearchInput] = useState("");

  const handleSearch = () => {
    if (onSearch && searchKey && searchInput !== "") {
      onSearch(searchKey, searchInput);
    }
  };

  const themeClasses =
    theme === "night"
      ? "bg-gray-700 text-gray-50"
      : "bg-primary-100 text-gray-700";

  return (
    <>
      <div className={`relative shadow-3xl rounded-3xl ${themeClasses}`}>
        {searchFields && (
          <div className="flex flex-wrap items-center justify-between p-4 gap-4">
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`w-[70%] rounded-xl p-2 border ${
                theme === "night"
                  ? "bg-gray-800 text-gray-200 border-gray-600"
                  : "bg-white text-gray-700 border-primary-300"
              }`}
            />

            <select
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              className={`w-[15%] rounded-xl p-2 border ${
                theme === "night"
                  ? "bg-gray-800 text-gray-200 border-gray-600"
                  : "bg-white text-gray-700 border-primary-300"
              }`}
            >
              {searchFields.map((field) => (
                <option key={field} value={field}>
                  {humanizeField(field)}
                </option>
              ))}
            </select>

            <button
              onClick={handleSearch}
              className={`w-[10%] flex items-center gap-x-2 py-2 px-4 rounded-full 
                ${theme === "night" ? "text-gray-50" : "text-gray-800"} 
                bg-primary hover:bg-primary-600`}
            >
              <span className="material-icons">search</span>
              Search
            </button>
          </div>
        )}
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`relative shadow-3xl rounded-3xl ${themeClasses} lg:overflow-x-clip`}
      >
        <div className="overflow-x-auto lg:overflow-x-clip max-h-[800px] rounded-t-3xl">
          <table className="w-full max-w-full min-w-[900px] lg:min-w-full table-fixed text-sm text-left rtl:text-right">
            <thead
              className={`text-xs uppercase ${
                theme === "night"
                  ? "bg-gray-700 text-gray-400"
                  : "bg-primary-100 text-gray-700"
              } `}
              style={{ position: "sticky", top: 0, zIndex: 1 }}
            >
              <tr>
                <th className="p-4 sticky left-0 bg-inherit z-10">S/N</th>
                {columns.map((column, index) => (
                  <th key={index} className="px-6 py-3 whitespace-nowrap">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <motion.tbody
              initial="hidden"
              animate="visible"
              variants={tableVariants}
            >
              {loading ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : data.length > 0 ? (
                data.map((row, rowIndex) => (
                  <motion.tr
                    key={rowIndex}
                    variants={rowVariants}
                    className={`border-b hover:bg-gray-50 ${
                      theme === "night"
                        ? "bg-gray-800 border-gray-700 hover:bg-gray-600 text-gray-50"
                        : "bg-primary-50 border-gray-300 text-gray-700"
                    }`}
                  >
                    <td className="p-4 sticky left-0 bg-inherit z-10">
                      {rowIndex +
                        1 +
                        ((pagination?.page ?? 1) - 1) *
                          (pagination?.page_size ?? 10)}
                    </td>
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-6 py-1 break-words whitespace-normal ${
                          column.isAction
                            ? "sticky right-0 bg-inherit z-10"
                            : ""
                        }`}
                      >
                        {column.isAction && column.actionButtons ? (
                          <ActionMenu
                            row={row}
                            actionButtons={column.actionButtons}
                            isOpen={openMenuIndex === rowIndex}
                            onToggle={() => handleMenuToggle(rowIndex)}
                          />
                        ) : column.render ? (
                          column.render(row)
                        ) : (
                          row[column.accessor!]
                        )}
                      </td>
                    ))}
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-4">
                    No data available
                  </td>
                </tr>
              )}
            </motion.tbody>
          </table>
        </div>
        {pagination && (
          <nav
            className={`flex flex-col sm:flex-row items-center justify-between gap-4 p-4 text-sm ${
              theme === "night" ? "text-gray-400" : "text-gray-700"
            }`}
            aria-label="Table navigation"
          >
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm font-bold">
                Items per page:
              </label>
              <select
                id="items-per-page"
                className={`px-3 py-2 rounded-xl border ${
                  theme === "night"
                    ? "bg-gray-800 text-gray-400 border-gray-600"
                    : "bg-primary-50 text-gray-700 border-primary-300"
                }`}
                onChange={(e) =>
                  pagination.onPageSizeChange(Number(e.target.value))
                }
                value={pagination.page_size}
                title="Select items per page"
              >
                {[5, 10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="items-per-page" className="text-sm font-bold">
                Total Items:
              </label>
              <span className="text-sm leading-none">
                {pagination.total_items}
              </span>
            </div>
            <ul className="inline-flex items-center space-x-1">
              <li>
                <button
                  onClick={() => pagination.onPageChange(1)}
                  disabled={pagination.is_first_page}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    pagination.is_first_page
                      ? "cursor-not-allowed text-gray-400"
                      : theme === "night"
                      ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                      : "bg-primary-50 text-gray-500 hover:bg-primary-200 border-primary-300"
                  }`}
                  title="Go to the first page"
                >
                  <span
                    className="material-icons text-lg leading-none"
                    style={{ fontSize: "20px", width: "20px" }}
                  >
                    skip_previous_rounded
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => pagination.onPageChange(pagination.page - 1)}
                  disabled={pagination.is_first_page}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    pagination.is_first_page
                      ? "cursor-not-allowed text-gray-400"
                      : theme === "night"
                      ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                      : "bg-primary-50 text-gray-500 hover:bg-primary-200 border-primary-300"
                  }`}
                  title="Go to the previous page"
                >
                  <span
                    className="material-icons text-lg leading-none"
                    style={{ fontSize: "20px", width: "20px" }}
                  >
                    chevron_left
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => pagination.onPageChange(pagination.page + 1)}
                  disabled={pagination.is_last_page}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    pagination.is_last_page
                      ? "cursor-not-allowed text-gray-400"
                      : theme === "night"
                      ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                      : "bg-primary-50 text-gray-500 hover:bg-primary-200 border-primary-300"
                  }`}
                  title="Go to the next page"
                >
                  <span
                    className="material-icons text-lg leading-none"
                    style={{ fontSize: "20px", width: "20px" }}
                  >
                    chevron_right
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => pagination.onPageChange(pagination?.last_page)}
                  disabled={pagination.is_last_page}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    pagination.is_last_page
                      ? "cursor-not-allowed text-gray-400"
                      : theme === "night"
                      ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                      : "bg-primary-50 text-gray-500 hover:bg-primary-200 border-primary-300"
                  }`}
                  title="Go to the last page"
                >
                  <span
                    className="material-icons text-lg leading-none"
                    style={{ fontSize: "20px", width: "20px" }}
                  >
                    skip_next_rounded
                  </span>
                </button>
              </li>
            </ul>
          </nav>
        )}
      </motion.div>
    </>
  );
};

const ActionMenu: React.FC<{
  row: any;
  actionButtons: ActionButton[];
  isOpen: boolean;
  onToggle: () => void;
}> = ({ row, actionButtons, isOpen, onToggle }) => {
  const { theme } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 150 + window.scrollX,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onToggle();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const menuVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  };

  const authService = useAuth();
  const filteredButtons = actionButtons.filter((button) => {
    return !button.permissions || authService.hasPermission(button.permissions);
  });

  return (
    <>
      <button
        ref={buttonRef}
        className={`p-2 rounded-full ${
          theme === "night" ? "hover:bg-gray-700" : "hover:bg-gray-200"
        }`}
        onClick={onToggle}
      >
        <span className="material-icons">more_vert</span>
      </button>
      {isOpen &&
        createPortal(
          <motion.div
            ref={menuRef}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={menuVariants}
            className={`absolute shadow-lg rounded-2xl backdrop-blur-2xl ${
              theme === "night" ? "bg-gray-800/80" : "bg-gray-50/80"
            }`}
            style={{
              zIndex: 1050,
              position: "absolute",
              top: menuPosition.top,
              left: menuPosition.left,
              width: "150px",
            }}
          >
            {filteredButtons.map((button) => (
              <motion.button
                key={button.id}
                variants={itemVariants}
                className={`flex items-center gap-2 w-full text-left px-4 py-2 text-lg${
                  theme === "night"
                    ? "text-gray-300 hover:bg-gray-700 rounded-2xl"
                    : "text-gray-700 hover:bg-gray-100 rounded-2xl"
                } ${button.class}`}
                onClick={() => {
                  button.action(row);
                  onToggle();
                }}
              >
                {button.icon && (
                  <span className={`material-icons text-lg ${button.class}`}>
                    {button.icon}
                  </span>
                )}
                {button.label}
              </motion.button>
            ))}
          </motion.div>,
          document.body
        )}
    </>
  );
};

export default PaginatedTable;
